import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';

import Slider from 'react-slick-adaptive-height-fixed';

import 'slick-carousel/slick/slick.css';

import { useApplicationContext } from '@common/react/components/Core/Application/Application';
import { ItemProvider, useItemProviderContext } from '@common/react/components/Core/ItemProvider/ItemProvider';
import { imageSize } from '@common/react/utils/utils';
import { Lang } from '@common/typescript/objects/Lang';
import { ItemEditorWrapper } from '@common/react/components/Core/ItemEditor/ItemEditorWrapper';
import { List } from '@common/typescript/objects/List';
import { BreadCrumbs } from '@common/react/components/UI/BreadCrumbs/BreadCrumbs';
import ServerPageProvider from '@common/react/components/UI/lang/SeverPageProviderWithLang/ServerpageProviderWithLang';

import { AmenityTypeNames } from '@commonTuna/react/objects/Amenity';
import { AmenityTypeIcons } from '@commonTuna/react/objects/AmenityImages';

import { Location, PageLocation } from '@commonTuna/react/objects/BaseLocation';
import { Review } from '@commonTuna/react/objects/Review';

import ShareLinks from '@app/components/UI/ShareLinks/ShareLinks';
import DoctorCard from '@app/components/Pages/Home/DoctorCard';
import { reviewsSliderSettings, sliderSettings } from '@app/components/Utils';
import ContactUsForm from '@app/components/Forms/ContactUs/ContactUsForm';
import Reviews from '@app/components/UI/Reviews/Reviews';
import { OfficeInfoBlock } from '@app/components/Pages/Home/OfficeInfoBlock';
import Rate from '@app/components/UI/Rate/Rate';
import PageLocations from '@app/components/Pages/Clinic/PageLocations';

import '@app/scss/pages/doctorPage.scss';
import '@app/scss/components/slick-theme.scss';
import '@app/scss/pages/clinic.scss';

interface DoctorInnerProps {
	load: () => Promise<any>;
}

interface LocationWithReviews extends Location {
	reviews: Array<Review>;
}

const onClick = (id) => {
	document.getElementById(id)
		?.scrollIntoView({ behavior: 'smooth', block: 'center' });
};

const ClinicInner: React.FC<DoctorInnerProps> = ({ load }) => {
	const location = useLocation();

	const [skipLoad, setSkipLoad] = React.useState(true);
	const { getLang } = useApplicationContext();
	const lang = getLang();

	const { state: { item } } = useItemProviderContext<LocationWithReviews>();

	const { i18n, t } = useTranslation();
	const isEnglish = i18n.language === 'en';

	const name = isEnglish ? item.nameEn : item.nameEs;

	const avatars = [item.siteAvatar, ...item.files.map((q) => q.file.src)];

	React.useEffect(() => {
		const newPath = lang === Lang.Es ? `/es/clinic/${item?.pathEs}` : `/clinic/${item?.pathEn}`;
		if (!skipLoad && newPath !== location.pathname) {
			load();
		}
		setSkipLoad(false);
	}, [location.pathname]);

	return (
		<div className="doctor-page inner-page container">
			<div className="clearfix mb10">
				<BreadCrumbs title={name} />
				<ShareLinks />
			</div>
			<div className="clearfix">
				{avatars.length > 0 && <div className="doctor-page__image">
					<Slider {...sliderSettings} dots>
						{avatars.map((avatar) => <div key={avatar} className="square-block">
							<div className="doctor-page__image_inner">
								<img src={imageSize(avatar, '_main')} alt="Clinic avatar" />
							</div>
						</div>)}
					</Slider>
				</div>}
				<div className="doctor-page__body">
					<div className="doctor-page__name inner-page__headline">
						<h1>
							{name}
						</h1>
						<div onClick={() => onClick('reviews')} className="pull-right">
							<span className="rate-value">
								{item.averageReviewRate.toLocaleString('en', { maximumFractionDigits: 2 })}
							</span>
							<Rate hideEmpty value={item.averageReviewRate} />
							<span className="review-count">
								{' '}
								{item.totalReviewCount}
								{' '}
								reviews
							</span>
						</div>
						<div className="empty-line" />
					</div>
					<div
						className="doctor-page__description external-html"
						dangerouslySetInnerHTML={{ __html: isEnglish ? item.textEn : item.textEs }}
					/>
				</div>
			</div>
			<div className="clearfix clinic-contact">
				<OfficeInfoBlock
					titleAsH2
					showWorkingHours
					office={item}
					content={<>
						{item.amenities.length > 0 ? <div className="amenities mb10">
							{item.amenities.map(({ amenityType }) => (
								<div key={amenityType} className="amenitiy">
									<img
										width="48px"
										height="48px"
										alt={AmenityTypeNames[amenityType]}
										title={AmenityTypeNames[amenityType]}
										src={AmenityTypeIcons[amenityType]}
									/>
								</div>
							))}
						</div> : null}
					</>}
				/>
			</div>
			<PageLocations
				items={{ list: item.pages, count: item.countPages } as List<PageLocation>}
				locationId={item.id}
			/>
			<div className="clearfix doctor-card-list" style={{ marginTop: 30 }}>
				{item.doctors?.length > 0 ? <div className="row service-page__block mobile-hidden">
					{item.doctors?.map((item) => <DoctorCard key={item.id} item={item?.doctor} />)}
				</div> : null}
				{item.doctors?.length > 0 ? <div className="slider-arrows service-page__block mobile-show">
					<Slider {...reviewsSliderSettings} dots>
						{item.doctors.map((item) => <DoctorCard key={item.id} item={item?.doctor} />)}
					</Slider>
				</div> : null}
			</div>
			<Reviews id="reviews" reviews={item.reviews} />
			<div className="contacts-page__form">
				<div className="inner-page__headline text-center">
					<h2>{t('site.Schedule an Appointment Now')}</h2>
				</div>
				<ContactUsForm className="general-form" locationId={item.id} />
			</div>
		</div>
	);
};

const ClinicPageComponent: React.FC = () => {
	const { path } = useParams<{ path }>();

	return (
		<ServerPageProvider
			path="clinic"
			inner={(serverPage) =>
				<ItemEditorWrapper
					className=" "
					render={({ actions: { load } }) => <ItemProvider
						id={serverPage?.item?.id || 0}
						additionalParams={{ path, published: true, withProfessions: true }}
						loadRequest="getClinicBbyViewRemote"
						item={serverPage?.item}
					>
						<ClinicInner load={load} />
					</ItemProvider>}
					backPath="/"
				/>
			}
		/>
	);
};

export default ClinicPageComponent;
